<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { deviceCheckOpen, deviceCheckProgress, deviceCheckReport, settings } from '../store.js';
  import { runDeviceCheck } from '../../media/device-check-service.js';

  const dispatch = createEventDispatcher();
  let runId = 0;

  const labels = {
    camera: 'Camera',
    microphone: 'Microphone',
    codec: 'Recording codec',
    storage: 'Storage',
    nativeCapacity: 'Device storage source',
    thermal: 'Phone temperature',
    battery: 'Battery',
    network: 'Network',
    load: 'Estimated device load'
  };

  $: checks = $deviceCheckReport?.checks || {};
  $: reportStatus = $deviceCheckReport?.status || 'checking';
  $: storageBlocked = $deviceCheckReport?.blocking?.includes?.('storage') === true;

  function testCapabilities() {
    const injected = globalThis.__CRICKET_SCORER_TEST_CAPABILITIES__;
    const localHost = ['localhost', '127.0.0.1', '::1'].includes(globalThis.location?.hostname);
    return localHost && injected?.version === 1 ? (injected.deviceCheck || {}) : {};
  }

  async function runCheck() {
    const id = ++runId;
    deviceCheckProgress.set({ running: true, activeCheck: 'camera' });
    try {
      const report = await runDeviceCheck({ ...testCapabilities(), settings: $settings });
      if (id !== runId) return;
      deviceCheckReport.set(report);
    } catch (error) {
      if (id !== runId) return;
      deviceCheckReport.set({
        status: 'blocked',
        checkedAt: Date.now(),
        blocking: ['camera', 'microphone'],
        warnings: [],
        checks: {},
        metrics: {},
        error: error?.message || 'Device check failed'
      });
    } finally {
      if (id === runId) deviceCheckProgress.set({ running: false, activeCheck: null });
    }
  }

  function accept() {
    if (!$deviceCheckReport || $deviceCheckReport.status === 'blocked') return;
    const accepted = { ...$deviceCheckReport, acceptedAt: Date.now() };
    deviceCheckReport.set(accepted);
    deviceCheckOpen.set(false);
    dispatch('accept', accepted);
  }

  function close() {
    runId++;
    deviceCheckOpen.set(false);
    dispatch('close');
  }

  onMount(() => {
    runCheck();
  });
</script>

<div class="safety-backdrop" role="presentation">
  <div class="safety-modal device-check-modal" role="dialog" aria-modal="true" aria-labelledby="device-check-title">
    <header class="safety-heading">
      <div>
        <div class="safety-kicker">PRE-MATCH SAFETY</div>
        <h2 id="device-check-title">Device Check</h2>
        <p>Camera, mic, storage and recording load are checked before the match.</p>
      </div>
      <button class="safety-close" aria-label="Close device check" on:click={close}>×</button>
    </header>

    <div class="device-check-summary status-{reportStatus}" aria-live="polite">
      {#if $deviceCheckProgress.running}
        <strong>Checking your phone…</strong>
        <span>Allow camera and microphone access when asked.</span>
      {:else if reportStatus === 'ready'}
        <strong>Ready to record</strong>
        <span>Your device passed the recording checks.</span>
      {:else if reportStatus === 'warning'}
        <strong>Recording warnings found</strong>
        <span>You can continue, but review the warnings first.</span>
      {:else if reportStatus === 'blocked'}
        <strong>{storageBlocked ? 'More free storage is required' : 'Camera or microphone needs attention'}</strong>
        <span>{storageBlocked ? 'Recording stays blocked until the protected storage target is available.' : 'Recording stays blocked until both are available.'}</span>
      {/if}
    </div>

    <div class="diagnostic-grid">
      {#each Object.entries(labels) as entry (entry[0])}
        {@const check = checks[entry[0]] || { status: $deviceCheckProgress.running ? 'checking' : 'unavailable', message: 'Not checked yet' }}
        <article class="diagnostic-card check-{check.status}">
          <div class="diagnostic-card-title"><span>{entry[1]}</span><b>{check.status}</b></div>
          <p>{check.message || 'No details available'}</p>
        </article>
      {/each}
    </div>

    {#if $deviceCheckReport?.warnings?.length}
      <div class="safety-warning-list">
        {#each $deviceCheckReport.warnings as warning, i_each (i_each)}<div>⚠ {warning}</div>{/each}
      </div>
    {/if}

    <footer class="safety-actions">
      <button class="btn btn-ghost" disabled={$deviceCheckProgress.running} on:click={runCheck}>Check Again</button>
      {#if reportStatus === 'blocked'}
        <button class="btn btn-primary" disabled={$deviceCheckProgress.running} on:click={runCheck}>{storageBlocked ? 'Free Storage And Retry' : 'Fix Camera/Mic And Retry'}</button>
      {:else if reportStatus === 'warning'}
        <button class="btn btn-primary" disabled={$deviceCheckProgress.running} on:click={accept}>Continue Anyway</button>
      {:else}
        <button class="btn btn-primary" disabled={$deviceCheckProgress.running || reportStatus !== 'ready'} on:click={accept}>Continue</button>
      {/if}
    </footer>
  </div>
</div>
